import React from 'react';
import { useColorSettings } from '@/contexts/ColorSettingsContext';
import { colorPresentation } from './ColorVariantBadge';
import { NeedsLoadingProgress, useProgressiveNeeds } from './NeedsLoadingProgress';
import { materialNeedSummary } from '@/lib/materialNeedTotals';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { SimulationNeedsMatrix } from './SimulationNeedsMatrix';
import { buildSimulationNeedsMatrix, buildSimulationReportGroups } from '@/lib/simulationNeedsMatrix';

const number = value => Number(value || 0).toLocaleString('es-ES', { maximumFractionDigits: 4 });
export function SimulationNeedsViews({ model, beforeClose }) {
  const { colorSettings } = useColorSettings();
  const [view, setView] = React.useState(null);
  // Keep one consistent preview while it is open. Parent updates (for example,
  // colour settings finishing loading) must not restart the whole report.
  const [sourceModel, setSourceModel] = React.useState(model);
  const openView = nextView => {
    setSourceModel(model);
    setView(nextView);
  };
  const [prepared, setPrepared] = React.useState(null);
  const [progress, setProgress] = React.useState(0);
  const isOpen = Boolean(view);
  React.useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setPrepared(null); setProgress(0);
    (async () => {
      const rows = [];
      const columns = new Map();
      const items = sourceModel.items || [];
      for (let index = 0; index < items.length; index += 1) {
        if (index % 10 === 0) await new Promise(resolve => window.setTimeout(resolve, 0));
        if (cancelled) return;
        const part = buildSimulationNeedsMatrix({ items: [items[index]] });
        rows.push(...part.rows.map(row => ({ ...row, key: `${row.key}:${index}` })));
        for (const column of part.columns) {
          if (!columns.has(column.key)) columns.set(column.key, column);
          else columns.get(column.key).order = Math.min(columns.get(column.key).order, column.order);
        }
        if ((index + 1) % 10 === 0 || index === items.length - 1) setProgress(index + 1);
      }
      if (!cancelled) setPrepared({ rows, columns: [...columns.values()] });
    })();
    return () => { cancelled = true; };
  }, [sourceModel, isOpen]);
  const closing = React.useRef(false);
  const close = async () => {
    if (closing.current) return;
    closing.current = true;
    try {
      if (beforeClose && !(await beforeClose())) return;
      setView(null);
    } finally { closing.current = false; }
  };
  const groups = React.useMemo(() => buildSimulationReportGroups(prepared), [prepared]);
  const suppliers = React.useMemo(() => {
    const result = new Map();
    for (const group of groups) {
      if (!result.has(group.supplierKey)) result.set(group.supplierKey, { key: group.supplierKey, name: group.supplier, lines: [], materials: new Map() });
      const supplier = result.get(group.supplierKey);
      supplier.lines.push(group);
      const key = JSON.stringify([group.reference || group.name, group.unit]);
      if (!supplier.materials.has(key)) supplier.materials.set(key, { key, name: group.name, reference: group.reference, unit: group.unit, colors: [] });
      supplier.materials.get(key).colors.push(group);
    }
    return [...result.values()];
  }, [groups]);
  const rendering = useProgressiveNeeds(suppliers);
  return <>
    <div className="flex flex-wrap gap-2" role="group" aria-label="Vistas de necesidades">
      <Button variant="outline" onClick={() => openView('matrix')}>Cuadrícula de necesidades</Button>
      <Button variant="outline" onClick={() => openView('report')}>Informe interactivo · necesidades por proveedor</Button>
    </div>
    <Dialog open={Boolean(view)} onOpenChange={open => { if (!open) close(); }}>
      <DialogContent className="!left-0 !top-0 !translate-x-0 !translate-y-0 !w-screen !max-w-none !h-[100dvh] !rounded-none flex flex-col overflow-hidden">
        <Button type="button" variant="outline" className="w-fit" onClick={close}>← Volver</Button>
        <DialogTitle>{view === 'matrix' ? 'Cuadrícula de necesidades' : 'Necesidades por proveedor'} · Simulación</DialogTitle>
        <DialogDescription>Vista previa para valorar la fabricación. Los pedidos y su asignación se gestionan en la orden de fabricación una vez creada.</DialogDescription>
        <div className="flex gap-2"><Button variant={view === 'matrix' ? 'default' : 'outline'} onClick={() => setView('matrix')}>Ver cuadrante</Button><Button variant={view === 'report' ? 'default' : 'outline'} onClick={() => setView('report')}>Informe interactivo</Button></div>
        <div className="min-h-0 flex-1 overflow-auto" style={{ overflowAnchor: 'none' }} aria-busy={!prepared || (view === 'report' && rendering.loading)}>
          {!prepared ? <NeedsLoadingProgress label="Calculando necesidades de los productos…" current={progress} total={sourceModel.items?.length || 0} /> : view === 'matrix' ? <SimulationNeedsMatrix matrix={prepared} colorSettings={colorSettings} fullScreen /> : <>
            <div className="space-y-4">
              {rendering.loading && <NeedsLoadingProgress label="Cargando bloques de proveedores…" current={rendering.current} total={rendering.total} />}
              <div hidden={rendering.loading} className="space-y-4">
              {rendering.visible.map(supplier => <section key={supplier.key} className="space-y-3 rounded border border-blue-200 p-3">
                <header className="flex flex-wrap items-baseline justify-between gap-3"><h2 className="text-lg font-semibold">{supplier.name}</h2><span className="text-sm font-semibold tabular-nums">Necesidad total: {materialNeedSummary(supplier.lines, line => line.total, line => line.amount)}</span></header>
                {[...supplier.materials.values()].map(material => <section key={material.key} className="overflow-hidden rounded border">
                  <header className="bg-gray-100 p-3"><h3 className="font-semibold">{supplier.name} · {material.name}</h3><p className="text-sm">Ref. {material.reference || 'Sin referencia'} · {material.unit}</p></header>
                  <div className="overflow-auto"><table className="w-full min-w-[650px] table-fixed text-sm"><thead><tr><th className="w-1/4 p-3 text-left">Color del material</th><th className="p-3 text-left">Artículos / uso</th><th className="w-1/5 p-3 text-right">Necesidad</th></tr></thead><tbody>
                    {material.colors.map(group => {
                      const color = colorPresentation(group.color, colorSettings);
                      return <tr key={group.key} className="border-t align-top hover:bg-gray-50">
                        <td className="p-3 font-medium"><span className="flex min-h-10 items-center gap-2"><span className="h-6 w-6 shrink-0 rounded-full border border-gray-300" style={{ backgroundColor: color.hex || '#e5e7eb' }} aria-hidden="true" />{color.description}</span></td>
                        <td className="break-words p-3">{group.articles.map((article, index) => <div key={index}>{article.article} · {article.color_usage || article.name}: {number(article.needed)} {article.unit}</div>)}</td>
                        <td className="p-3 text-right tabular-nums"><div className="flex min-h-10 items-center justify-end">{number(group.total)} {group.unit}</div></td>
                      </tr>;
                    })}
                  </tbody></table></div>
                </section>)}
              </section>)}
              </div>
            </div>
            {!groups.length && <p>No hay necesidades de materiales.</p>}

          </>}
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
